import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { Risorsa, Progetto, Allocazione } from '../types';
import * as api from '../services/api';
import { useAuth } from './AuthContext';

interface PlanningContextType {
  anno: number;
  setAnno: (anno: number) => void;
  risorse: Risorsa[];
  progetti: Progetto[];
  allocazioni: Allocazione[];
  selectedRisorse: number[];
  selectedRisorsa: Risorsa | null;
  loading: boolean;
  error: string | null;
  setSelectedRisorsa: (risorsa: Risorsa | null) => void;
  addRisorsaToPlanning: (idRisorsa: number) => void;
  removeRisorsaFromPlanning: (idRisorsa: number) => void;
  saveAllocazione: (allocazione: Omit<Allocazione, 'id'>) => Promise<void>;
  deleteAllocazione: (id_risorsa: number, id_progetto: number, mese: number) => Promise<void>;
  refreshAllocazioni: () => Promise<void>;
  refreshProgetti: () => Promise<void>;
}

const PlanningContext = createContext<PlanningContextType | undefined>(undefined);

export const usePlanning = () => {
  const context = useContext(PlanningContext);
  if (!context) {
    throw new Error('usePlanning must be used within a PlanningProvider');
  }
  return context;
};

interface PlanningProviderProps {
  children: React.ReactNode;
}

export const PlanningProvider: React.FC<PlanningProviderProps> = ({ children }) => {
  const [anno, setAnno] = useState<number>(new Date().getFullYear());
  const [risorse, setRisorse] = useState<Risorsa[]>([]);
  const [progetti, setProgetti] = useState<Progetto[]>([]);
  const [allocazioni, setAllocazioni] = useState<Allocazione[]>([]);
  const [selectedRisorse, setSelectedRisorse] = useState<number[]>([]);
  const [selectedRisorsa, setSelectedRisorsa] = useState<Risorsa | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const { user } = useAuth();

  // Filtra risorse in base alle strutture dell'utente
  const filterRisorseByStrutture = useCallback((allRisorse: Risorsa[]): Risorsa[] => {
    if (!user) return [];

    // Se l'utente può vedere tutte le strutture, non filtra
    if (user.hasTutteLeStrutture) {
      return allRisorse;
    }

    // Filtra solo le risorse delle strutture associate all'utente
    return allRisorse.filter(risorsa =>
      user.strutture.includes(risorsa.struttura || '')
    );
  }, [user]);

  // Carica dati iniziali
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [risorseData, progettiResponse, allocazioniData] = await Promise.all([
          api.getRisorse(),
          api.getProgetti(false, 1, 50000), // Aumentato limit per caricare tutti i progetti
          api.getAllocazioni(anno),
        ]);

        // Filtra le risorse in base alle strutture dell'utente
        const risorseFiltrate = filterRisorseByStrutture(risorseData);

        setRisorse(risorseFiltrate);
        setProgetti(progettiResponse.data);
        setAllocazioni(allocazioniData);
      } catch (err) {
        setError('Errore nel caricamento dei dati');
        console.error('Error loading initial data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [anno, filterRisorseByStrutture]);

  const refreshAllocazioni = useCallback(async () => {
    try {
      const allocazioniData = await api.getAllocazioni(anno);
      setAllocazioni(allocazioniData);
    } catch (err) {
      console.error('Error refreshing allocazioni:', err);
    }
  }, [anno]);

  const refreshProgetti = useCallback(async () => {
    try {
      const progettiResponse = await api.getProgetti(false, 1, 50000); // Aumentato limit per caricare tutti i progetti
      setProgetti(progettiResponse.data);
    } catch (err) {
      console.error('Error refreshing progetti:', err);
    }
  }, []);

  const addRisorsaToPlanning = useCallback((idRisorsa: number) => {
    setSelectedRisorse((prev) => {
      if (!prev.includes(idRisorsa)) {
        return [...prev, idRisorsa];
      }
      return prev;
    });
  }, []);

  const removeRisorsaFromPlanning = useCallback((idRisorsa: number) => {
    setSelectedRisorse((prev) => prev.filter((id) => id !== idRisorsa));
  }, []);

  const saveAllocazione = useCallback(
    async (allocazione: Omit<Allocazione, 'id'>) => {
      try {
        await api.createOrUpdateAllocazione({
          id_risorsa: allocazione.id_risorsa,
          id_progetto: allocazione.id_progetto,
          anno: allocazione.anno,
          mese: allocazione.mese,
          percentuale: allocazione.percentuale,
        });
        await refreshAllocazioni();
      } catch (err: any) {
        const errorMessage = err.response?.data?.error || 'Errore nel salvataggio';
        throw new Error(errorMessage);
      }
    },
    [refreshAllocazioni]
  );

  const deleteAllocazione = useCallback(
    async (id_risorsa: number, id_progetto: number, mese: number) => {
      try {
        await api.deleteAllocazioneByParams(id_risorsa, id_progetto, anno, mese);
        await refreshAllocazioni();
      } catch (err) {
        console.error('Error deleting allocazione:', err);
        throw err;
      }
    },
    [anno, refreshAllocazioni]
  );

  const value: PlanningContextType = {
    anno,
    setAnno,
    risorse,
    progetti,
    allocazioni,
    selectedRisorse,
    selectedRisorsa,
    loading,
    error,
    setSelectedRisorsa,
    addRisorsaToPlanning,
    removeRisorsaFromPlanning,
    saveAllocazione,
    deleteAllocazione,
    refreshAllocazioni,
    refreshProgetti,
  };

  return <PlanningContext.Provider value={value}>{children}</PlanningContext.Provider>;
};
